import { ref, watch, computed } from '@vue/composition-api'
import store from '@/store'

// Notification
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { materiales } from '@/utils/materiales'
import useQuerySync from '@/utils/useQuerySync'
import useProveedoresList from '@/views/system/proveedor/useProveedoresList'

export default function useMaterialesList() {
  // Use toast
  const toast = useToast()

  const refMaterialesListTable = ref(null)

  // Table Handlers - ACTUALIZADO: Agregada columna fk_proveedor_nombre
  const tableColumns = [
    { key: 'codigo', label: 'Código', sortable: true },
    { key: 'descripcion', label: 'Descripción', sortable: true },
    { key: 'fk_proveedor_nombre', label: 'Proveedor', sortable: false },
    { key: 'coste', label: 'Coste', sortable: true },
    { key: 'stock_real', label: 'stock_real', sortable: true },
    { key: 'tipo', label: 'Tipo Material', sortable: true },
    { key: 'medida', label: 'Medida', sortable: true },
    { key: 'estado', label: 'Estado', sortable: true },
    { key: 'actions', label: 'Acciones', thStyle: { minWidth: '150px' } },
  ]
  const perPage = ref(50)
  const totalMateriales = ref(0)
  const currentPage = ref(1)
  const perPageOptions = [10, 25, 50, 100]
  const searchQuery = ref('')
  const statusFilter = ref(null)
  const providerFilter = ref(null)
  const providerOptions = ref([])
  const statusOptions = [
    { value: 'activo', label: 'Activo' },
    { value: 'inactivo', label: 'Inactivo' },
    { value: 'reparacion', label: 'Reparación' },
    { value: 'baja', label: 'Baja' },
  ]
  const sortBy = ref('id')
  const isSortDirDesc = ref(true)

  useQuerySync({
    search: searchQuery,
    status: statusFilter,
    provider: { ref: providerFilter, type: 'number' },
    page: currentPage,
    per_page: perPage,
  })

  const clearFilters = () => {
    searchQuery.value = ''
    statusFilter.value = null
    providerFilter.value = null
    currentPage.value = 1
    perPage.value = 50
    refetchData()
  }

  const dataMeta = computed(() => {
    const localItemsCount = refMaterialesListTable.value ? refMaterialesListTable.value.localItems.length : 0
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalMateriales.value,
    }
  })

  // Diccionario de proveedores para búsqueda rápida
  const providerMap = computed(() => {
    const map = {}
    providerOptions.value.forEach(p => {
      map[p.value] = p.label
    })
    return map
  })

  const refetchData = () => {
    if (refMaterialesListTable.value) {
      refMaterialesListTable.value.refresh()
    }
  }

  watch([currentPage, perPage, searchQuery, statusFilter, providerFilter], () => {
    refetchData()
  })

  const fetchMateriales = (ctx, callback) => {
    store
      .dispatch('app-materiales/fetchMateriales', {
        body: {
          search: searchQuery.value,
          paginate: perPage.value,
          estado: statusFilter.value,
          provider: providerFilter.value,
        },
        headers: {
          'X-Sort': `materiales.${sortBy.value}`,
          'X-Sort-Order': isSortDirDesc.value ? 'desc' : 'asc',
        },
        query: {
          page: currentPage.value,
        },
      })
      .then(res => {
        const { total } = res
        totalMateriales.value = total

        // Mapear materiales con nombre del proveedor
        const materialesConProveedor = res.data.map(material => ({
          ...material,
          fk_proveedor_nombre: material.proveedor ? material.proveedor.nombre_comercial : '-',
        }))

        callback(materialesConProveedor)
      })
      .catch(() => {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Error fetching materiales list',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  }

  const listMateriales = () => new Promise((resolve, reject) => {
    store
      .dispatch('app-materiales/listMateriales', {
        body: {
          search: searchQuery.value,
        },
        headers: {
          'X-Sort': 'materiales.descripcion',
          'X-Sort-Order': 'asc',
        },
        query: {},
      })
      .then(res => {
        const { total } = res
        totalMateriales.value = total
        const filterActive = res.data.filter(el => el.estado === 'activo')
        if (res.data.length > filterActive.length) {
          totalMateriales.value -= (res.data.length - filterActive.length)
        }
        resolve(filterActive)
      })
      .catch(e => {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Error fetching materiales list',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
        reject(e)
      })
  })

  const { listProveedores } = useProveedoresList({ enableWatchers: false })

  // Promesa para asegurar que los proveedores se cargan antes de mostrar la tabla
  const proveedoresLoadPromise = (async () => {
    try {
      const proveedores = await listProveedores()

      providerOptions.value = proveedores.map(p => ({
        value: p.id,
        label: p.nombre_comercial,
      }))

    } catch (e) {
      console.error('Error cargando proveedores', e)
      toast({
        component: ToastificationContent,
        props: {
          title: 'Error cargando proveedores',
          icon: 'AlertTriangleIcon',
          variant: 'danger',
        },
      })
    }
  })()

  // *===============================================---*
  // *--------- UI ---------------------------------------*
  // *===============================================---*

  const parseDate = date => {
    let d = date.substring(0, 10)
    d = d.split('-').reverse().join('-')
    const t = date.substring(12, 19)
    return `${d} ${t}`
  }

  const resolveStatusVariant = status => {
    if (status === 'activo') return 'success'
    if (status === 'inactivo') return 'danger'
    if (status === 'reparacion') return 'warning'
    if (status === 'baja') return 'info'
    return 'secondary'
  }

  const resolveStatusText = status => {
    if (status === 'activo') return materiales.estados[0].label
    if (status === 'inactivo') return materiales.estados[1].label
    if (status === 'reparacion') return materiales.estados[2].label
    if (status === 'baja') return materiales.estados[3].label
    return '-'
  }

  return {
    fetchMateriales,
    tableColumns,
    perPage,
    currentPage,
    totalMateriales,
    dataMeta,
    perPageOptions,
    providerOptions,
    searchQuery,
    providerFilter,
    statusOptions,
    sortBy,
    isSortDirDesc,
    refMaterialesListTable,

    refetchData,
    parseDate,
    resolveStatusVariant,
    resolveStatusText,
    listMateriales,

    // Extra Filters
    statusFilter,
    clearFilters,
  }
}
